import { Controller } from "@hotwired/stimulus"

// CONEK-3366 — Lightweight wrapper around the existing ai--companion
// sidebar markup. Owns the open/closed cookie so the sidebar's visibility
// survives page reloads.
//
// Usage:
//   <div data-controller="ai--companion-sidebar"
//        data-ai--companion-sidebar-open-value="<%= companion_open? %>"
//        data-ai--companion-sidebar-open-label-value="<%= t("ai.companion.toggle.open") %>"
//        data-ai--companion-sidebar-close-label-value="<%= t("ai.companion.toggle.close") %>">
//     <%= render AI::Companion::SidebarComponent.new(...) %>
//   </div>
// openLabel/closeLabel (workstream 3 sub-phase 5) are REQUIRED once a
// toggleButton target is present — syncToggleButton() sets its aria-label
// straight from these values with no hardcoded-English fallback, so a mount
// missing them renders an empty aria-label instead of "Open/Close AI Assistant".

// CONEK-3367 AC3 — Global keyboard map for the AI Companion, and since the
// GA-programme cutover the ONLY one: the React CompanionSidebar that used to
// mirror it is deleted, so this controller is the single owner of Cmd/Ctrl+J
// and Escape for the companion (LayoutProvider deliberately binds neither —
// see app/frontend/shared/contexts/layout-context.tsx).
// Guarded against drift by the committed companion_keyboard_map.snapshot.json
// via __tests__/companion_keyboard_map.test.ts.
// CONEK-5614 — Cmd/Ctrl+K is reserved for the global search dialog; the
// Companion opens/closes with Cmd/Ctrl+J (toggle) and must not bind K.
export const KEYBOARD_MAP = Object.freeze({
  close: "Escape",
  toggle: "j",
  submit: "Enter",
})

function isEditableTarget(el) {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  )
}

// Style classes applied to the navbar toggle button. Kept in sync with
// the server-rendered classes in app/views/layouts/support.html.erb so
// first paint matches the cookie-driven openValue and subsequent toggles
// keep the button visually in sync with the sidebar state.
const TOGGLE_BUTTON_OPEN_CLASSES = ["bg-primary/10", "text-primary"]
const TOGGLE_BUTTON_CLOSED_CLASSES = [
  "text-muted-foreground",
  "hover:text-foreground",
  "hover:bg-muted",
]

// Fallback bounds, identical to AI::Companion::PanelWidth::LEGACY. The server
// normally hands the controller its bounds as values (min/max/default-width),
// which is how `ai.companion_panel_v2` widens the panel without a second
// copy of the numbers living here.
const MIN_WIDTH = 320
const MAX_WIDTH = 720
const DEFAULT_WIDTH = 420
const WIDTH_COOKIE = "companion_width"
// WCAG 2.1.1 — ArrowLeft/ArrowRight step for the keyboard-operable resize
// handle (mouse-drag already supports any pixel value).
const RESIZE_KEYBOARD_STEP = 20

export default class extends Controller {
  static values = {
    open: Boolean,
    openLabel: String,
    closeLabel: String,
    // Desktop's navbar toggle button is a plain ghost/muted button, so
    // TOGGLE_BUTTON_OPEN_CLASSES/CLOSED_CLASSES make sense layered onto it.
    // The mobile FAB (mobile.html.erb) already has its OWN filled-primary
    // styling (Ui::Mobile::FabComponent) — layering the desktop classes on
    // top of that produced broken/unpredictable icon color. Set false to
    // keep aria-expanded/aria-label in sync (still always updated) without
    // touching the button's classList.
    colorSyncedToggle: { type: Boolean, default: true },
    minWidth: { type: Number, default: MIN_WIDTH },
    maxWidth: { type: Number, default: MAX_WIDTH },
    defaultWidth: { type: Number, default: DEFAULT_WIDTH },
  }
  // `aside` target is the actual sidebar element whose visibility we
  // toggle via Tailwind translate-x / hidden classes. Without this the
  // sidebar would be permanently visible (server-rendered fixed
  // position).
  static targets = ["aside", "resizeHandle", "toggleButton", "widthToggle"]

  connect() {
    // CONEK-3367 AC3 — Window-level keydown listener mirrors React's
    // global shortcuts (Esc close, Cmd/Ctrl+J toggle). Cmd/Ctrl+K is NOT
    // bound — it belongs to the global search dialog (CONEK-5614).
    // Editable-target guard prevents stealing keys from text inputs.
    this._handleKeydown = (event) => this.handleGlobalKeydown(event)
    window.addEventListener("keydown", this._handleKeydown)
    // GA programme F2.3 — inbound half of the React↔Hotwire open/closed
    // bridge. React code that still needs to drive the companion (the drawing
    // editor's drawer exclusion in shared/drawer.tsx, and the "Ask AI" toolbar
    // action in toolbar/use-basic-handlers.ts) calls
    // useLayout().setCompanionOpen, which now dispatches this event instead of
    // holding its own copy of the state. Without it, those call sites toggled
    // React state that no longer renders anything.
    this._handleSetOpen = (event) => {
      const next = event?.detail?.open
      if (typeof next !== "boolean") return
      if (this.openValue === next) return
      this.openValue = next
    }
    window.addEventListener("companion:set-open", this._handleSetOpen)
    // A Turbo morph refresh (companion_controller.js#onRecordChanged, the
    // live refresh after a companion write) morphs <body>'s attributes back
    // to the server render, which drops the inline padding-right that keeps
    // the page clear of the open panel. Re-apply it.
    this._handleMorph = () => this.syncBackgroundPush()
    document.addEventListener("turbo:morph", this._handleMorph)
    // Resize state (mouse-drag handlers are wired on demand in startResize).
    this._handleResizeMove = (event) => this.handleResizeMove(event)
    this._handleResizeEnd = () => this.handleResizeEnd()
    // Hydrate width from cookie so the sidebar reopens at the same size
    // it was last left at.
    const stored = this.readStoredWidth()
    this.applyWidth(stored)
    // Sync the initial visibility + push state in case the page loads
    // with openValue=true.
    this.syncAsideVisibility()
    this.syncBackgroundPush()
    this.syncToggleButton()
  }

  disconnect() {
    if (this._handleKeydown) {
      window.removeEventListener("keydown", this._handleKeydown)
      this._handleKeydown = null
    }
    if (this._handleSetOpen) {
      window.removeEventListener("companion:set-open", this._handleSetOpen)
      this._handleSetOpen = null
    }
    if (this._handleMorph) {
      document.removeEventListener("turbo:morph", this._handleMorph)
      this._handleMorph = null
    }
    if (this._handleResizeMove) {
      window.removeEventListener("mousemove", this._handleResizeMove)
      this._handleResizeMove = null
    }
    if (this._handleResizeEnd) {
      window.removeEventListener("mouseup", this._handleResizeEnd)
      this._handleResizeEnd = null
    }
    // Restore body layout in case the controller unmounts while open
    // (Turbo navigation, hot reload).
    document.body.style.paddingRight = ""
  }

  // Workstream 3 sub-phase 7 (cutover) — on Inertia pages the toggleButton
  // target is React's navbar AI-Assistant button, rendered client-side
  // AFTER Vite/React boot. It can connect well after this controller's own
  // connect() already ran (and returned early from syncToggleButton() via
  // !hasToggleButtonTarget), so the button's aria-label/expanded/color
  // state must also be synced on the target's OWN connect — not just once,
  // eagerly, in connect() above.
  toggleButtonTargetConnected() {
    this.syncToggleButton()
  }

  handleGlobalKeydown(event) {
    const isMod = event.metaKey || event.ctrlKey
    const editable = isEditableTarget(event.target)

    if (isMod && event.key === KEYBOARD_MAP.toggle) {
      event.preventDefault()
      this.openValue = !this.openValue
      return
    }
    if (event.key === KEYBOARD_MAP.close && !editable && this.openValue) {
      this.openValue = false
    }
  }

  toggle() {
    this.openValue = !this.openValue
  }

  openValueChanged(value, previousValue) {
    document.cookie = `companion_open=${this.openValue}; path=/; SameSite=Lax`
    this.syncAsideVisibility()
    this.syncBackgroundPush()
    this.syncToggleButton()
    // GA programme F2.3 — outbound half of the bridge. This controller is the
    // SINGLE source of truth for open/closed (it owns the cookie, the aside
    // classes, the body push and Cmd+J). React's LayoutProvider mirrors this
    // event so `useLayout().companionOpen` stays accurate for consumers that
    // react to it — chiefly the drawing editor's drawer, which must close when
    // the companion opens. Fired on the initial connect too (previousValue
    // === undefined) so a page loading with the companion already open starts
    // the mirror in the right state.
    this.dispatchState()
    // Notify the chat-list rail to auto-collapse, but ONLY on a genuine
    // open transition (header button / Cmd+J) — not on the
    // initial connect (previousValue === undefined), so reopening the
    // page with the companion already open doesn't clobber the user's
    // persisted expand/collapse choice.
    if (value && previousValue !== undefined) {
      window.dispatchEvent(new CustomEvent("companion:opened"))
    }
    // Focus management (workstream 3 sub-phase 3), skipped on the initial
    // connect for the same reason as above — only a genuine toggle should
    // move focus. Mirrors the React reference's returnFocusRef effect
    // (CompanionSidebar.tsx): remember what was focused before opening so
    // closing can put it back, and move focus into the composer on open.
    if (previousValue === undefined) return
    if (value) {
      const active = document.activeElement
      this._returnFocusEl =
        active instanceof HTMLElement && active !== document.body
          ? active
          : null
      // Wait for the hidden->flex class swap (syncAsideVisibility, just
      // above) to finish applying so the textarea is actually focusable.
      setTimeout(() => {
        this.asideTarget
          ?.querySelector('[data-ai--companion-target="input"]')
          ?.focus()
      }, 300)
    } else {
      this._returnFocusEl?.focus()
      this._returnFocusEl = null
    }
  }

  // Mirror React's side-by-side layout: shrink the viewport by the
  // sidebar's width so navbar + main reflow next to it instead of being
  // covered. The aside is `position:fixed` so it sits outside the normal
  // flow; we add right-padding to the entire body to reserve the space.
  // (Applying to <main> alone would leave the navbar overlapped.)
  // Broadcasts the canonical open/closed state and current width so React's
  // LayoutProvider can mirror them (see openValueChanged). Width is emitted on
  // open/close and at the END of a resize — NOT per mousemove: the drawing
  // editor's drawer reads companionWidth to compute its right offset, and a
  // React re-render on every mouse move is exactly the rubber-banding
  // isResizingCompanion was introduced to avoid.
  dispatchState() {
    window.dispatchEvent(
      new CustomEvent("companion:state", {
        detail: { open: this.openValue, width: this.currentWidth() },
      }),
    )
  }

  syncBackgroundPush() {
    const w = this.currentWidth()
    // Matches the aside's own slide (duration-300 ease-out) so the page
    // reflow and the panel move as one surface instead of two.
    document.body.style.transition = "padding-right 300ms ease-out"
    document.body.style.paddingRight = this.openValue ? `${w}px` : ""
  }

  // Begin a mouse-drag resize. Bound to mousedown on the resize handle.
  // We capture pointer state on `this` and attach window listeners so the
  // drag continues even if the cursor leaves the handle's bounds.
  startResize(event) {
    if (!this.hasAsideTarget) return
    event.preventDefault()
    this._resizing = true
    document.body.style.cursor = "col-resize"
    // Disable selection while dragging — feels janky otherwise.
    document.body.style.userSelect = "none"
    window.addEventListener("mousemove", this._handleResizeMove)
    window.addEventListener("mouseup", this._handleResizeEnd)
  }

  handleResizeMove(event) {
    if (!this._resizing) return
    // The aside is anchored to the right edge; width = (viewport - clientX).
    const w = Math.min(
      this.maxWidthValue,
      Math.max(this.minWidthValue, window.innerWidth - event.clientX),
    )
    this.applyWidth(w)
    this.syncBackgroundPush()
  }

  handleResizeEnd() {
    if (!this._resizing) return
    this._resizing = false
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    window.removeEventListener("mousemove", this._handleResizeMove)
    window.removeEventListener("mouseup", this._handleResizeEnd)
    // Persist whatever the final value happened to be.
    document.cookie = `${WIDTH_COOKIE}=${this.currentWidth()}; path=/; SameSite=Lax; max-age=31536000`
    this.dispatchState()
  }

  // WCAG 2.1.1 — the handle was mousedown-only (a WAI-ARIA APG "separator"
  // needs Left/Right, and Home/End to jump to the min/max, once it's a
  // moveable splitter). The sidebar is anchored to the right edge, so
  // ArrowLeft (dragging the handle away from the edge) WIDENS it and
  // ArrowRight NARROWS it — the same direction the mouse-drag uses.
  handleResizeKeydown(event) {
    let next = null
    if (event.key === "ArrowLeft")
      next = this.currentWidth() + RESIZE_KEYBOARD_STEP
    else if (event.key === "ArrowRight")
      next = this.currentWidth() - RESIZE_KEYBOARD_STEP
    else if (event.key === "Home") next = this.minWidthValue
    else if (event.key === "End") next = this.maxWidthValue
    else return

    event.preventDefault()
    this.#commitWidth(next)
  }

  // Double-click on the resize handle — back to the default width.
  resetWidth() {
    this.#commitWidth(this.defaultWidthValue)
  }

  // The header expand control: jump to the widest width, or back to the
  // default when already there.
  toggleWide() {
    const wide = this.currentWidth() >= this.maxWidthValue
    this.#commitWidth(wide ? this.defaultWidthValue : this.maxWidthValue)
  }

  #commitWidth(width) {
    this.applyWidth(width)
    this.syncBackgroundPush()
    document.cookie = `${WIDTH_COOKIE}=${this.currentWidth()}; path=/; SameSite=Lax; max-age=31536000`
    this.dispatchState()
  }

  applyWidth(w) {
    const clamped = Math.min(
      this.maxWidthValue,
      Math.max(this.minWidthValue, w),
    )
    this.element.style.setProperty("--companion-width", `${clamped}px`)
    // The header expand control points the way its next click goes.
    if (this.hasWidthToggleTarget) {
      this.widthToggleTarget.setAttribute(
        "aria-pressed",
        String(clamped >= this.maxWidthValue),
      )
    }
    if (this.hasAsideTarget) {
      this.asideTarget.style.width = `${clamped}px`
    }
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.setAttribute("aria-valuenow", String(clamped))
    }
  }

  currentWidth() {
    if (this.hasAsideTarget) {
      const px = parseInt(this.asideTarget.style.width || "", 10)
      if (Number.isFinite(px) && px > 0) return px
    }
    return this.defaultWidthValue
  }

  readStoredWidth() {
    const m = document.cookie.match(
      new RegExp("(?:^|; )" + WIDTH_COOKIE + "=([^;]+)"),
    )
    const n = m?.[1] ? parseInt(m[1], 10) : NaN
    return Number.isFinite(n) ? n : this.defaultWidthValue
  }

  // Keep the navbar toggle button visually aligned with openValue. The
  // server renders the button with one of two class sets based on the
  // cookie, but every subsequent toggle happens client-side so the
  // classes have to be flipped here. Without this the icon stays blue
  // (or stays muted) regardless of the actual sidebar state.
  syncToggleButton() {
    if (!this.hasToggleButtonTarget) return
    const btn = this.toggleButtonTarget
    if (this.colorSyncedToggleValue) {
      if (this.openValue) {
        btn.classList.remove(...TOGGLE_BUTTON_CLOSED_CLASSES)
        btn.classList.add(...TOGGLE_BUTTON_OPEN_CLASSES)
      } else {
        btn.classList.remove(...TOGGLE_BUTTON_OPEN_CLASSES)
        btn.classList.add(...TOGGLE_BUTTON_CLOSED_CLASSES)
      }
    }
    btn.setAttribute("aria-expanded", String(this.openValue))
    btn.setAttribute(
      "aria-label",
      this.openValue ? this.closeLabelValue : this.openLabelValue,
    )
  }

  // Mirror server-rendered initial state classes
  // (sidebar_component.html.erb): when collapsed, the aside has
  // `translate-x-full hidden`; when open it has `flex translate-x-0`.
  // This keeps cookie-driven reloads + interactive toggles aligned.
  // The aside stays `display:flex` permanently and animates via transform +
  // visibility (both declared in its `transition-[transform,visibility]`
  // class). `visibility` is the off-state gate instead of `hidden`: it is
  // transition-aware (flips at the END of the slide-out, so the panel stays
  // visible while it leaves) and, like display:none, removes the closed panel
  // from hit-testing and the tab order. Toggling `hidden` here — as this used
  // to — killed the transition entirely: the panel popped in/out with no
  // motion.
  // CONEK-5131 G6 — `inert` is toggled together with `aria-hidden`, never
  // apart from it. axe-core's `aria-hidden-focus` rule requires an
  // aria-hidden element to contain no focusables, and this aside holds
  // several. `invisible` alone does not cover it: visibility flips at the END
  // of the slide-out (see the comment above), so mid-animation the panel is
  // aria-hidden and still tabbable.
  //
  // Removing `inert` on open is not optional — leaving it on would make the
  // opened panel completely non-interactive. Server-side initial state is set
  // in sidebar_component.html.erb; this keeps the toggle in step with it.
  syncAsideVisibility() {
    if (!this.hasAsideTarget) return
    const aside = this.asideTarget
    if (this.openValue) {
      aside.classList.remove("translate-x-full", "invisible")
      aside.classList.add("translate-x-0", "visible")
      aside.setAttribute("aria-hidden", "false")
      aside.removeAttribute("inert")
    } else {
      aside.classList.add("translate-x-full", "invisible")
      aside.classList.remove("translate-x-0", "visible")
      aside.setAttribute("aria-hidden", "true")
      aside.setAttribute("inert", "")
    }
  }
}
